import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Row} from "reactstrap";
import {ReactComponent as BlogDateIcon} from "../../../../../../../assets/images/content/blog-date-icon.svg";
import {ReactComponent as BlogViewsIcon} from "../../../../../../../assets/images/content/blog-views-icon.svg";
import classnames from "classnames";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ArrowBack} from "@mui/icons-material";
import ShareContent from "../../../../../../components/app-specific/content/share";
import {Paper} from "@mui/material";
import ContentRelatedTopicsSlider from "../../../../../../components/base/slider/content-related-topics";
import ContentVideo from "../../../../../../components/app-specific/content/video";
import YoutubeVideoPlayer, {VideoTypes} from "../../../../../../components/base/video-player/youtube";
import NativeVideoPlayer from "../../../../../../components/base/video-player/native";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../../core/services/date-utils";
import {useHistory, useParams} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";

const ContentVideoInformationView = () => {
    const params = useParams();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [loadingRelatedBlogs, setLoadingRelatedVideos] = useState(false);
    const [video, setVideo] = useState({});
    const [relatedVideos, setRelatedVideos] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('content-video-information-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const isVideoFromYoutube = useMemo(() => video?.url?.startsWith('https://www.youtube') ?? false, [video?.url]);

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.contentVideoInformation(params.slug));
        configureBehavioralTool((tags) => tags.contentVideoInformation(params.slug));
    }, [configureBehavioralTool, configureNotebookTags, params.slug]);


    /**
     * Scrolls to the top of the page as soon as the slug changes
     */
    useEffect(() => {
        const mainSection = document.querySelector('main');
        mainSection?.scrollTo({left: 0, top: 0, behavior: 'smooth'})
    }, [params.slug])

    /**
     * Loads the related blogs of the current video by searching with blogs' tags and if the result of the api is
     * successful, sets the data
     */
    const getRelatedVideos = useCallback(async (video) => {
        setLoadingRelatedVideos(true);
        const forApi = {
            paginationInfo: {currentPage: 1, pageSize: 20},
            filters: {tagsId: video?.tags?.map(e => e.id)}
        };
        const response = await Api.searchVideos(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setRelatedVideos(response.data?.resultList?.filter(e => e.id !== video.id) ?? []);
        }
        setLoadingRelatedVideos(false)
    }, [isMounted]);

    /**
     * Loads the full information of the specified blog based on its slug and if the result of the api is
     * successful, sets the data
     */
    const getFullInformation = useCallback(async () => {
        setLoading(true);
        const response = await Api.getVideoBySlug(params.slug);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag && response.data) {
            setVideo(response.data);
            void getRelatedVideos(response.data);
        }
    }, [getRelatedVideos, isMounted, params.slug]);

    /**
     * As soon as the component mounts, it calls the function for getting the full information of the blog
     */
    useEffect(() => {
        void getFullInformation();
    }, [getFullInformation])

    return (
        <>
            <Row className={'justify-content-around align-items-start px-4 content-single'}>
                {
                    loading
                        ? (
                            <>
                                <div className={'d-flex w-100 justify-content-between align-items-start'}>
                                    <div className={'d-flex flex-column w-50 w-lg-25'}>
                                        <div className={'loading extraSmall mb-2'}>
                                            <div/>
                                        </div>
                                        <div className={'loading extraExtraSmall'}>
                                            <div/>
                                        </div>
                                    </div>
                                    <div className={'w-10'}>
                                        <div className={'loading extraExtraSmall'}>
                                            <div/>
                                        </div>
                                    </div>
                                </div>
                                <div className={'w-100 mt-4'}/>
                                <div className={'w-100 loading blog-main-image mb-4'}>
                                    <div/>
                                </div>
                                <div className={'d-flex w-100'}>
                                    <div className={'w-50 loading extraSmall mb-5'}>
                                        <div/>
                                    </div>
                                </div>

                                <div className={'w-100 loading extraExtraSmall'}>
                                    {
                                        Array(20).fill(null).map((e, index) => (
                                            <div key={index}/>
                                        ))
                                    }
                                </div>
                            </>
                        )
                        : (
                            <>
                                <div className={'d-flex w-100 justify-content-between align-items-start'}>
                                    <div className={'d-flex flex-column w-75'}>
                                        <div className={'d-flex w-100 justify-content-start' +
                                            ' align-items-center'}>
                                            <IconButton

                                                onPressed={() => history.goBack()}
                                                color={'paper.secondary'}
                                            >
                                                <ArrowBack className='icon normal'/>
                                            </IconButton>
                                            <h2 className={'title m-0 ml-3'}>
                                                {video.title ?? ''}
                                            </h2>
                                        </div>
                                        <div className={'d-flex w-100 justify-content-start' +
                                            ' align-items-center mb-1 ml-4'}>
                                            <p className={'date ml-4 mr-4'}>
                                                <BlogDateIcon className={'mr-1 ml-1'}/>
                                                {
                                                    video?.lastModifyDateTime
                                                        ? DateUtils.serverToLocal(video?.lastModifyDateTime).format('DD MMM YYYY')
                                                        : ''
                                                }
                                            </p>
                                            <p className={'date'}>
                                                <BlogViewsIcon className={'mr-2'}/>
                                                {`${video.viewCount ?? 0}`}
                                            </p>
                                        </div>

                                    </div>
                                    <ShareContent title={video?.slug} body={'Here\'s the link for this blog:'}/>
                                </div>
                                <div className={'w-100 mt-4'}/>
                                <div className={'blog-video mb-4'}>
                                    {
                                        isVideoFromYoutube
                                            ? <YoutubeVideoPlayer
                                                key={video.id}
                                                id={`video-${video.id}`}
                                                videoId={video.url?.split('/')[video.url?.split('/').length - 1]}
                                                type={VideoTypes.large}
                                                title={video.title}
                                            />
                                            : <NativeVideoPlayer
                                                key={video.id}
                                                id={`video-${video.id}`}
                                                src={video.url}
                                                type={VideoTypes.large}
                                                title={video.title}
                                            />
                                    }

                                </div>
                                <div className={'w-100 mt-3'}/>
                                <Paper className={'paper w-100 flex-wrap px-4 pb-5'}>
                                    <div className={'w-100 mx-5 mt-2'}>
                                        {
                                            video?.otherPropValues?.length > 0 &&
                                            <>
                                                <div className={'w-100 mt-4'}/>
                                                <p className={'features'}>Videos's features:</p>
                                                <div className={'w-100 mb-3'}/>
                                            </>
                                        }
                                    </div>
                                    <div className={'w-100 mb-3'}/>
                                    <div className={'d-flex flex-wrap w-100 mt-4 mx-5'}>
                                        {
                                            video?.otherPropValues?.map((e, index) => (
                                                <div
                                                    key={e.property?.name ?? index}
                                                    className={classnames('blog-property-box', {'no-border': index === video?.otherPropValues?.length - 1})}>
                                                    <div className={classnames('ball')}/>

                                                    <div className={'d-flex flex-column flex-wrap'}>
                                                        <h4 className={'title mb-1'}>
                                                            {e?.property?.name ?? ''}
                                                        </h4>
                                                        <p className={'value'}>
                                                            {e?.value ?? ''}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>
                                    <div className={'w-100 my-2'}/>
                                    <div className={'w-100 body mx-3'}
                                         dangerouslySetInnerHTML={{__html: video?.description ?? ''}}
                                    />
                                </Paper>
                            </>
                        )
                }
                <Paper className={'paper flex-wrap p-4 pb-3 my-4 w-100'}>
                    <div className={'w-100 mx-3 mb-3'}>
                        <h2 className={'title'}>
                            Related Topics
                        </h2>
                    </div>
                    {
                        loadingRelatedBlogs
                            ? <div className={'w-100 d-flex flex-row p-0'} style={{overflowX: 'scroll'}}>
                                {
                                    Array(8).fill(null).map((e, index) => (
                                        <div key={index} className={'loading video-loading'}>
                                            <div/>
                                        </div>
                                    ))
                                }
                            </div>
                            : <div className={'related-topics'}>
                                <ContentRelatedTopicsSlider
                                    content={relatedVideos.map((video) => (
                                        <ContentVideo key={video.id} data={video}/>
                                    ))}
                                />
                            </div>
                    }
                </Paper>
            </Row>
        </>
    );
}


export default ContentVideoInformationView;
